import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

export default function InvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    api
      .get(`/invites/${token}`)
      .then(({ data }) => setInvite(data))
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || "Invite not valid"));
  }, [token]);

  const accept = async () => {
    if (!user) {
      // Send the user to register, then back to /invite/:token
      navigate(`/register?next=/invite/${token}`);
      return;
    }
    setAccepting(true);
    try {
      const { data } = await api.post(`/invites/${token}/accept`);
      toast.success(data.already_member ? "Already a member" : "You joined the board");
      navigate(`/boards/${data.board_id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to accept");
    } finally {
      setAccepting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white px-4">
      <div className="w-full max-w-md border border-zinc-200 p-6 sm:p-10">
        <div className="font-mono-tight text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">
          board invite
        </div>
        {error ? (
          <>
            <h1 className="font-display text-2xl sm:text-3xl font-black tracking-tighter mb-2">
              Invite invalid.
            </h1>
            <p className="text-sm text-zinc-500 mb-6">{error}</p>
            <button
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-2 border border-zinc-200 px-4 py-2.5 text-sm hover:border-black"
            >
              Go home
            </button>
          </>
        ) : !invite ? (
          <div className="text-sm text-zinc-500 font-mono-tight uppercase tracking-widest">
            Loading invite...
          </div>
        ) : (
          <>
            <h1 className="font-display text-2xl sm:text-3xl font-black tracking-tighter mb-2">
              Join “{invite.board_title}”.
            </h1>
            <p className="text-sm text-zinc-500 mb-8">
              You've been invited to collaborate on this board. It currently has{" "}
              <span className="font-bold text-zinc-900">{invite.members}</span> member
              {invite.members === 1 ? "" : "s"}.
            </p>
            <button
              onClick={accept}
              disabled={accepting}
              className="group inline-flex items-center justify-center gap-2 bg-black text-white px-6 py-3.5 text-sm font-medium hover:bg-zinc-800 transition-colors disabled:opacity-50 w-full sm:w-auto"
              data-testid="invite-accept-button"
            >
              {user ? (accepting ? "Joining..." : "Accept invite") : "Sign up to join"}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
            {!user && (
              <p className="text-xs text-zinc-400 mt-4">
                Already have an account?{" "}
                <button
                  onClick={() => navigate(`/login?next=/invite/${token}`)}
                  className="text-zinc-900 underline underline-offset-2"
                  data-testid="invite-login-link"
                >
                  Sign in
                </button>
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
